import { Base64 } from "../../../library-js/crypt/Base64";
import { AudioHelper } from "../../../library-js/audio/AudioHelper";
import { Timer } from "../../../library-js/tools/Timer";
import { URLHash } from "../../../library-js/dom/URLHash";
import { RadioInfoInput } from "../php/input/RadioInfoInput";
import { SongInput } from "../php/input/SongInput";
import { RadioInfo } from "../php/request/RadioInfo";
import { SongRequest } from "../php/request/SongRequest";
import { RadioChannels, RadioChannelType } from "./data/RadioChannel";
import { RadioUI } from "./RadioUI";
import { PlayingSongInfo } from "./data/PlayingSongInfo";
import { Rokojori } from "../Rokojori";
import { OnClick, OnFocus, OnMouseDown, OnMouseMove, OnTouchMove, OnTouchStart } from "../../../library-js/dom-tools/EventListeners";
import { ElementAttribute } from "../../../library-js/dom-tools/ElementAttribute";
import { ClassFlag } from "../../../library-js/dom-tools/ClassFlag";
import { SVGRotate } from "../../../library-js/animation/SVGRotate";
import { Weekdays } from "./data/Weekday";
import { RadioPlayer } from "./player/RadioPlayer";
import { ElementType } from "../../../library-js/dom-tools/ElementType";
import { SongInfo } from "./data/SongInfo";
import { UserAgentInfo } from "../../../library-js/dom/UserAgentInfo";
import { OverlayHint, OverlayHintOptions } from "../overlay/OverlayHint";
import { HTMLEditor } from "../../../library-js/dom-tools/HTMLEditor";
import { RadioNextSong, RadioNextSongInput } from "../php/request/RadioNextSong";

export class Radio
{
  static _instance:Radio = null;
  static readonly clickInfo = new ElementAttribute( "click-info" );
  static readonly rotater = new ElementAttribute( "rotater" );
  static readonly wheel = new ElementType( "radio-ui-wheel" );


  private static _radioActivated = false;
  static _hashTitleMapping = new Map<string,PlayingSongInfo>();
  static _songHashMap = new Map<string,SongInfo>();

  static attachedGlobalListener = false;

  static get $()
  {
    if ( Radio._instance )
    {
      return Radio._instance;
    } 

    Radio._instance = new Radio();

    return Radio._instance;
  }

  static stop()
  {
    if ( ! Radio._instance )
    {
      return;
    }

    Radio._instance.stopPlaying();
  }


  static addGlobalListeners()
  {
    if ( this.attachedGlobalListener )
    {
      return;
    }

    this.attachedGlobalListener = true;



    window.addEventListener( "popstate", ()=>
    {
      let radioUIElement = RadioUI.elementType.queryDoc() as HTMLElement;

      if ( ! radioUIElement )
      {
        return;
      }

      if ( ! Radio._radioActivated )
      {
        console.log( "RADIO NOT ACTIVATED" );
        return;
      }

      console.log( "HASH CHANGED" );

      Radio.$.playSongHash( URLHash.get() );
  
    } 
    );
  }

  static applyOnDocument()
  {
    this._radioActivated = false;
    this.addGlobalListeners();
    
    let radioUIElement = RadioUI.elementType.queryDoc() as HTMLElement;

    Radio.stop();
    
    if ( ! radioUIElement )
    {
      return;
    }

    if ( UserAgentInfo.isIPad || UserAgentInfo.isIPhone )
    { 
      RadioUI.setSimpleMode( radioUIElement );
    }

    let started = false;

    Radio.clickInfo.forEachInDoc(
      ( ci )=>
      {
        if ( started )
        {
          return;
        }

        OnClick.add( ci as HTMLElement, 
          ()=>
          { 
            Radio.showBetaHint();
            Radio.$.start( radioUIElement );   
          }
        )
        
      }
    )
  }

  static async showBetaHint()
  {
    var title = "BETA Hint";

    var localizedContent = 
    {
      en: 
      `
        Keep the browser tab 
        <br>
        focused/visible for <b>AUTO PLAY</b>. 
        <br><br>

        Unfocused/hidden tabs  <br>
        unfortunately won't play new <br>
        songs until refocused. 

        <br><br>
        😓 Sorry for that, 
        <br>
        we are working on a solution.
        <br><br>

        ❤️ROKOJORI❤️

        <br><br>
      `,

      de: 
      `
        Halte das Browser-Fenster 
        <br>
        fokusiert/sichtbar für <b>AUTO PLAY</b>. 
        
        <br><br>
        Unfokusierte/versteckte Tabs spielen 
        <br>
        leider keine neuen Songs ab bis <br>
        sie wieder fokusiert werden.

        <br><br>
        😓 Sorry dafür,
        <br> 
        wir arbeiten an einer Lösung.
        
        <br><br>

        ❤️ROKOJORI❤️
        <br><br>

        
      `
    }
    ;

    var language = Rokojori.$.pageHandler.currentLanguage as ( "en" | "de" );
    var content = "";

    if ( localizedContent[ language ] )
    {
      content = localizedContent[ language ];
    }
    else
    {
      content = localizedContent.en;
    }

    

    let options = new OverlayHintOptions();
    options.isCancable = false;
    options.isClosable = true;
    options.onConfirm.addListener( ()=>{} );

    OverlayHint.hint( title, HTMLEditor.stringToWrappedElement( content ), options );
  }

  static getSongInfoByHash( hash:string )
  {
    return this._songHashMap.get( hash );
  }

  static async getSongInfo( channel:RadioChannelType, hash:string )
  {
    if ( this._hashTitleMapping.has( hash ) )
    {
      return this._hashTitleMapping.get( hash );
    }

    let request   = new RadioInfo();
    let inputData = new RadioInfoInput();
    
    inputData.weekday = Weekdays.today; 
    inputData.time    = Weekdays.time; 
    inputData.channel = channel ;
    inputData.search  = hash;
    
    try
    {
      
      let response = await request.request( Rokojori.phpContext, inputData );

      if ( response.playingSongs[ 0 ].hashTitle === hash )
      {
        this._hashTitleMapping.set( hash, response.playingSongs[ 0 ] );
      }
      else
      {
        this._hashTitleMapping.set( hash, null );
      }

      
    }
    catch ( e )
    {
      console.log( e) ;
    }
    
    return this._hashTitleMapping.get( hash );
  } 

  static async getNextSongInfo( channel:RadioChannelType ):Promise<SongInfo>
  {
    let request   = new RadioNextSong();
    let inputData = new RadioNextSongInput();
    
    inputData.played = Radio.$._player.playedHistory;
    inputData.channel = channel ;
    
    try
    {
      
      let response = await request.request( Rokojori.phpContext, inputData );

      if ( ! Radio._songHashMap.has( response.songInfo.songHash ) )
      {
        Radio._songHashMap.set( response.songInfo.songHash, response.songInfo );
      }
      
      return response.songInfo;
    }
    catch ( e )
    {
      console.log( e) ;
    } 
  }


  
  static async getRandomSongInfo( channel:RadioChannelType ):Promise<PlayingSongInfo>
  {
    let request   = new RadioInfo();
    let inputData = new RadioInfoInput();
    
    inputData.weekday = Weekdays.today; 
    inputData.time    = Weekdays.time; 
    inputData.channel = channel ;
    
    try
    {
      
      let response = await request.request( Rokojori.phpContext, inputData );

      response.playingSongs.forEach( 
        ( p )=>
        { 
          if ( ! Radio._hashTitleMapping.has( p.hashTitle ) )
          {
            Radio._hashTitleMapping.set( p.hashTitle, p );
          }
        }
      )

      return this._hashTitleMapping.get( response.playingSongs[ 0 ].hashTitle );
    }
    catch ( e )
    {
      console.log( e) ;
    } 
  }




  // INSTANCE








  private _channel:RadioChannelType = RadioChannels.BEATS;
  private _ui:RadioUI;
  
  get ui()
  {
    return this._ui;
  }

  private _player:RadioPlayer;

  get player(){ return this._player; }

  _isPlaying = false;
  _uiUpdateTimer = new Timer( 2 );
  

  constructor()
  {
    this._initialize();
  }

  private _initialize()
  {
    this._player = new RadioPlayer();
    this._player.initialize();    
    this._update();
  }

  
  private _update()
  {
    if ( this._player )
    {
      let playing = this._player.isPlaying;

      if ( playing !== this._isPlaying )
      {
        this._isPlaying = playing;
        this._ui.onPlayPauseChange();
      }
    }

    this._updateElements();

    requestAnimationFrame( ()=>{ this._update(); } );
  }

  

  private stopPlaying()
  {
    if ( ! this._player )
    {
      return;      
    }

    this._player.stop();
  }

  private _updateElements()
  {
    this._player.update();   

    this._uiUpdateTimer.update();

    if ( this._uiUpdateTimer.finished )
    {
      this._ui.setPlayState( this._player.playedSeconds, this._player.playState );        
    }

    if ( ! this._uiUpdateTimer.running )
    {
      this._uiUpdateTimer.start();
    }
        
  }

  start( element:Element )
  {
    Radio._radioActivated = true;
    
    Radio.clickInfo.queryDocAll().forEach(
      ( clickInfo:SVGElement ) =>
      {
        clickInfo.style.opacity = "0";
        clickInfo.style.pointerEvents = "none";
      }
    )

    this._ui = new RadioUI( element );
    this._channel = this._ui.channel;
    this._player.channel = this._channel;
    this._ui.setVolume( this._player.mainVolume );
    this._ui.setHiFi( 0, 0 );

    console.log( "STARTING RADIO CHANNEL", this._channel );
    
    this.startPlayer();
  }
  
  async startPlayer()
  {
    let hash = URLHash.get();    
    
    console.log( "CHECKING HASH", hash );

    let info = await Radio.getSongInfo( this._channel, hash );

    if ( info )
    {
      console.log( "FOUND HASH", hash, info );
      this.playSongHash( info.hashTitle );
    }
    else
    {
      console.log( "NO HASH FOUND, PLAYING RANDOM", hash, info );
      this._player.scheduleNext();
    }
    
  }

  playSongHash( songHash:string )
  {
    let songInfo = Radio.getSongInfoByHash( songHash ); 
    console.log( "PLAYING", songInfo );

    if ( songInfo == null )
    {
      this._player.scheduleNext();
      return;
    }

    this._ui.setLeftInfo( songInfo );
    this._player.playSong( songInfo );
  }

  /*async playSong( songHashTitle:string )
  {
    if ( songHashTitle === null || songHashTitle === undefined || songHashTitle === "" )
    {
      console.log( "UNDEFINED SONG HASH", songHashTitle );
      return;
    }

    let songInfo = await Radio.getSongInfo( this._channel, songHashTitle );

    if ( songInfo === null )
    {
      console.log( "RETURNS INVALID SONG INFO: ", songHashTitle );
      return;
    }

    console.log( "PLAYING", songInfo );

    this._ui.setLeftInfo( songInfo.songInfo );
    this._player.play( songInfo );
  }
  */


}